import React from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SyncProvider } from "./context/SyncContext";
import { OnlinePresenceProvider } from "./context/OnlinePresenceContext";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import OreSimple from "./pages/OreSimple";
import Personal from "./pages/Personal";
import Tagesraporte from "./pages/Tagesraporte";
import Daune from "./pages/Daune";
import RaportPSA from "./pages/RaportPSA";
import PersonDetail from "./pages/PersonDetail";
import Autoturisme from "./pages/Autoturisme";
import CarDetail from "./pages/CarDetail";
import Remorci from "./pages/Remorci";
import RemorcaDetail from "./pages/RemorcaDetail";
import { ConfirmProvider } from "./components/ConfirmProvider";
import PlanificatorConcediu from "./pages/PlanificatorConcediu";
import Inventar from "./pages/Inventar";
import PreluatDinInventar from "./pages/PreluatDinInventar";
import EchipamentAlocat from "./pages/EchipamentAlocat";
import ConfirmariMultiSantier from "./pages/ConfirmariMultiSantier";
import Facturare from "./pages/Facturare";
import LoanedEquipment from "./pages/LoanedEquipment";
import SiteLeftoversPage from "./pages/inventar/SiteLeftoversPage";
import Proiecte from "./pages/Proiecte";
import Management from "./pages/Management";
import Settings from "./pages/Settings";
import Chirii from "./pages/Chirii";
import Avansuri from "./pages/Avansuri";
import Combustibil from "./pages/Combustibil";
import ConsumuriMasini from "./pages/ConsumuriMasini";
import Planificator from "./pages/Planificator";
import HoursOverview from "./pages/HoursOverview";
import Tasks from "./pages/Tasks";
import Asistent from "./pages/Asistent";
import Cronologie from "./pages/Cronologie";
import DoagaIstoric from "./pages/DoagaIstoric";
import ServiceLog from "./pages/ServiceLog";
import FiseOre from "./pages/FiseOre";
import ChipAlimentare from "./pages/ChipAlimentare";
import RapoarteCombustibil from "./pages/RapoarteCombustibil";
import IstoricDemontari from "./pages/IstoricDemontari";
import IstoricReparatii from "./pages/IstoricReparatii";
import IntretinereMasini from "./pages/IntretinereMasini";
import IstoricIntretinere from "./pages/IstoricIntretinere";
import KhreisTask from "./pages/KhreisTask";
import VideoExplicative from "./pages/VideoExplicative";
import Documente from "./pages/Documente";
import ActeFirma from "./pages/ActeFirma";
import GenericModule from "./pages/GenericModule";
import ErrorBoundary, { installGlobalErrorReporting } from "./components/ErrorBoundary";

// Activează o singură dată raportarea globală a erorilor frontend.
installGlobalErrorReporting();

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="h-screen flex items-center justify-center text-zinc-500">Se încarcă...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function DashboardSwitch() {
  const { user } = useAuth();
  // Angajat și Magazioner văd EmployeeDashboard; admin și planificator văd Dashboard standard
  if (user?.role === "user" || user?.role === "magazioner") {
    return <EmployeeDashboard />;
  }
  return <Dashboard />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <ConfirmProvider>
        <SyncProvider>
        <OnlinePresenceProvider>
        <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><Layout /></Protected>}>
            <Route index element={<DashboardSwitch />} />
            <Route path="dashboard-angajat" element={<EmployeeDashboard />} />
            <Route path="ore-simple" element={<OreSimple />} />
            <Route path="personal" element={<Personal />} />
            <Route path="personal/:id" element={<PersonDetail />} />
            <Route path="tagesraport" element={<Tagesraporte />} />
            <Route path="preluat-din-inventar" element={<PreluatDinInventar />} />
            <Route path="echipament-alocat" element={<EchipamentAlocat />} />
            <Route path="confirmari-multi-santier" element={<ConfirmariMultiSantier />} />
            <Route path="facturare" element={<Facturare />} />
            <Route path="daune" element={<Daune />} />
            <Route path="raport-psa" element={<RaportPSA />} />
            <Route path="autoturisme" element={<Autoturisme />} />
            <Route path="autoturisme/:id" element={<CarDetail />} />
            <Route path="remorci" element={<Remorci />} />
            <Route path="remorci/:id" element={<RemorcaDetail />} />
            <Route path="concediu" element={<PlanificatorConcediu />} />
            <Route path="inventar" element={<Inventar />} />
            <Route path="inventar/imprumuturi" element={<LoanedEquipment />} />
            <Route path="inventar/santier" element={<SiteLeftoversPage />} />
            <Route path="proiecte" element={<Proiecte />} />
            <Route path="management" element={<Management />} />
            <Route path="chirii" element={<Chirii />} />
            <Route path="avansuri" element={<Avansuri />} />
            <Route path="combustibil" element={<Combustibil />} />
            <Route path="consumuri-masini" element={<ConsumuriMasini />} />
            <Route path="rapoarte/combustibil" element={<RapoarteCombustibil />} />
            <Route path="istoric-demontari" element={<IstoricDemontari />} />
            <Route path="istoric-reparatii" element={<IstoricReparatii />} />
            <Route path="intretinere-masini" element={<IntretinereMasini />} />
            <Route path="istoric-intretinere" element={<IstoricIntretinere />} />
            <Route path="planificator" element={<Planificator />} />
            <Route path="ore" element={<HoursOverview />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="asistent" element={<Asistent />} />
            <Route path="cronologie" element={<Cronologie />} />
            <Route path="doaga-istoric" element={<DoagaIstoric />} />
            <Route path="service-log" element={<ServiceLog />} />
            <Route path="fise-ore" element={<FiseOre />} />
            <Route path="chip-alimentare" element={<ChipAlimentare />} />
            <Route path="khreis-task" element={<KhreisTask />} />
            <Route path="video-explicative" element={<VideoExplicative />} />
            <Route path="documente" element={<Documente />} />
            <Route path="acte-firma" element={<ActeFirma />} />
            <Route path="settings" element={<Settings />} />
            <Route path="modul/:code" element={<GenericModule />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </OnlinePresenceProvider>
      </SyncProvider>
      </ConfirmProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
